/**
 * Core Module Routes
 * Basic routes that don't belong to specific modules
 */

import { lazy } from "react";
import { Navigate } from "react-router-dom";
import { ModuleRoutes } from "../types";

// Lazy load components
const Home = lazy(() => import("@/pages/Home"));
// NotFound externo: reporta o caminho pro errorTracker (aparece em
// /admin/erros-sistema) além de renderizar a tela.
const NotFound = lazy(() => import("@/pages/NotFound"));
const ExecutiveDashboard = lazy(() => import("@/pages/ExecutiveDashboard"));
const PresentationMode = lazy(() => import("@/pages/dashboard/PresentationMode"));
const OnboardingKanban = lazy(() => import("@/pages/onboarding/Kanban"));
const BuscaHistorico = lazy(() => import("@/pages/BuscaHistorico"));
const AlertManagementPage = lazy(() => import("@/pages/AlertManagementPage"));
// Story E1.6.3 — self-service preferences (manage_own). Substitui a v1
// `NotificationPreferencesPage` que era admin global (manage_all). O
// fluxo admin pra editar prefs de outros users virá em Epic E1.8.
const NotificationPreferencesPage = lazy(
  () => import("@/pages/notifications-center/PreferencesPage")
);
const NotificationHistoryPage = lazy(() => import("@/pages/NotificationHistoryPage"));
const LogHistory = lazy(() => import("@/pages/LogHistory"));
const SinistrosGestaoPage = lazy(() =>
  import("@/pages/sinistros/SinistrosGestaoPage").then((m) => ({ default: m.SinistrosGestaoPage }))
);
const WorkflowDesigner = lazy(() => import("@/pages/workflows/Designer"));
const WorkflowInbox = lazy(() => import("@/pages/workflows/Inbox"));
const WorkflowApprovalsPage = lazy(() => import("@/pages/workflows/WorkflowApprovalsPage"));
const DocumentVersionsPage = lazy(() => import("@/pages/documents/DocumentVersionsPage"));
const UnauthorizedPage = lazy(() =>
  import("@/pages/Unauthorized").then((m) => ({ default: m.UnauthorizedPage }))
);
// O organograma da empresa. Mesma tela do RH, em modo leitura — ver a
// prop `somenteLeitura` no componente.
const OrganogramaEmpresa = lazy(() => import("@/pages/rh/VisualizacaoOrganograma"));
// A roleta do sorteio (biometria facial, evento 25/09/2026). `lazy` como as
// vizinhas: fora da janela do evento ninguém abre esta tela, e ela carrega a
// Roleta junto.
const Premio = lazy(() => import("@/pages/Premio"));

export const coreRoutes: ModuleRoutes = {
  moduleId: "core",
  routes: [
    // Basic routes
    {
      path: "/",
      element: <Home />,
    },
    {
      path: "/unauthorized",
      element: <UnauthorizedPage />,
    },
    // 🔑 SEM `permission`: o organograma é da empresa, não do RH. Quem
    //    edita continua precisando de `rh:organograma_view` na rota de
    //    dentro do RH; aqui é só olhar.
    {
      path: "/organograma",
      element: <OrganogramaEmpresa somenteLeitura />,
    },
    // 🔑 SEM `permission`: o sorteio é de quem cadastrou o próprio rosto, não
    //    uma capacidade administrativa. Exigir permissão obrigaria a semear
    //    papel novo para as ~150 pessoas da sexta. Quem não está na base
    //    congelada abre a tela e é avisado disso — ver `pages/Premio.tsx`.
    //    A permissão `sorteio:manage` gateia o outro lado (criar e liberar a
    //    campanha), que é REST + `scripts/biometria_campanha.py`.
    {
      path: "/premio",
      element: <Premio />,
    },
    // ⚠️ /dashboard era uma página de números inventados ("1,234
    //    colaboradores", "3,789 terminais") — removida em 14/09/2026. A rota
    //    fica, levando ao Início: endereço salvo não pode virar 404.
    {
      path: "/dashboard",
      element: <Navigate to="/" replace />,
    },
    {
      path: "/dashboard/executive",
      element: <ExecutiveDashboard />,
    },
    // Modo Apresentação (PRD 1 §2.9 — Story 1.9.3): tela de TV/fullscreen pra
    // sala de operações. Sem permission guard — telão de operações fica logado
    // num user de display dedicado.
    {
      path: "/dashboard/presentation-mode",
      element: <PresentationMode />,
    },
    // Onboarding
    {
      path: "/onboarding/kanban",
      element: <OnboardingKanban />,
      permission: { resource: "onboarding", action: "analytics:view" },
    },
    // Busca
    {
      path: "/busca/historico",
      element: <BuscaHistorico />,
    },
    // Alertas
    {
      path: "/alerts",
      element: <AlertManagementPage />,
      permission: { resource: "alertas", action: "view" },
    },
    // Story E1.6.3 — self-service preferences. Todo user autenticado com
    // perm `notifications:preferences:manage_own` (seed BASE_USER) acessa.
    // Backend GET /me usa current_user.id automaticamente.
    {
      path: "/notifications/preferences",
      element: <NotificationPreferencesPage />,
      permission: { resource: "notifications", action: "preferences:manage_own" },
    },
    {
      path: "/notifications/history",
      element: <NotificationHistoryPage />,
      permission: { resource: "notificacoes", action: "view" },
    },
    // Logs
    {
      path: "/logs/history",
      element: <LogHistory />,
    },
    // Sinistros
    {
      path: "/sinistros/gestao",
      element: <SinistrosGestaoPage />,
      permission: { resource: "sinistro", action: "gerenciar" },
    },
    // Workflows
    {
      path: "/workflows/designer",
      element: <WorkflowDesigner />,
      permission: { resource: "workflows", action: "designer_view" },
    },
    {
      path: "/workflows/inbox",
      element: <WorkflowInbox />,
      permission: { resource: "workflows", action: "inbox_view" },
    },
    {
      path: "/workflows/approvals",
      element: <WorkflowApprovalsPage />,
      permission: { resource: "workflows", action: "inbox_view" },
    },
    {
      path: "/documentos/:id/versoes",
      element: <DocumentVersionsPage />,
      permission: { resource: "documentos_listagem", action: "view" },
    },
    // 404 - must be last
    {
      path: "*",
      element: <NotFound />,
    },
  ],
};
